import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { BlindData, Frames, Operation } from '@root/app.interfaces';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { BREAKPOINTS, STORAGE_NAMES } from '@root/app.config';

export type Image = {
	name: string;
	type: 'square' | 'round' | 'full';
	url: string;
	loading?: boolean;
};

export type BlindSetupItem = {
	id: number;
	name: string;
	image?: string;
	images?: { url: string }[];
};

@Component({
	selector: 'app-blind-images',
	templateUrl: './blind-images.component.html',
	styleUrls: ['./blind-images.component.scss', './blind-images.responsive.scss'],
})
export class BlindImagesComponent implements OnInit, OnChanges {
	@Input() blind: BlindData;

	protected readonly BREAKPOINTS = BREAKPOINTS;
	protected images: Image[] = [];

	constructor(private sessionStorageService: SessionStorageService) {}

	ngOnInit() {
		this.getImages();
	}

	ngOnChanges() {
		this.getImages();
	}

	private getImages(): void {
		const config = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_config);
		const frameConfig = config[this.blind.type].frame;
		const frameSetup = this.blind.setup.frames;
		const operationSetup = this.blind.setup.operation;

		const images: Image[] = [
			{ name: 'main', type: 'full', url: this.blind.blind_img_url, loading: true },
			{ name: 'top_style', type: 'round', url: this.getImageUrl(frameConfig.top_style, frameSetup.top_style), loading: true },
			{ name: 'bottom_bar', type: 'round', url: this.getImageUrl(frameConfig.bottom_bar_protection, frameSetup.bottom_bar), loading: true },
			{ name: 'operation_main', type: 'square', url: this.getImageUrl(frameConfig.operation, operationSetup, 0), loading: true },
			{ name: 'operation_secondary', type: 'square', url: this.getImageUrl(frameConfig.operation, operationSetup, 1), loading: true },
		];

		// Add Insect Control System (if top_style === Streamline Pelmet)
		if (frameSetup.top_style.id === 4) {
			const image: Image = {
				name: 'insect_control_system',
				type: 'round',
				url: this.getImageUrl(frameConfig.top_style, frameSetup.top_style, 1),
			};

			images.splice(2, 0, image);
		}

		// When 'Spring Balanced with Centre Lock Release' selected
		if (operationSetup.id === 1 && operationSetup.optional.length) {
			const start = this.getOperationStartIndex(frameSetup, operationSetup);
			const operationImages = operationSetup.optional.map((o) => ({
				name: o.name,
				type: 'square',
				url: this.getImageUrl(frameConfig.operation[0].optional, o, 0),
				loading: true,
			})) as Image[];

			images.splice(start, 1, ...operationImages);
		}

		if (images.length === 6) {
			images.pop();
		}

		this.images = images;
	}

	// Defines the insertion index
	private getOperationStartIndex(frameSetup: Frames, operationSetup: Operation): number {
		const hasTwoOptionals = operationSetup.optional.length === 2;
		const hasStreamlinePelmet = frameSetup.top_style.id === 4;

		return 4 + Number(hasTwoOptionals) + Number(hasStreamlinePelmet) * 2 - Number(hasStreamlinePelmet && hasTwoOptionals) * 2;
	}

	private getImageUrl(firstProperty: BlindSetupItem[], secondProperty: BlindSetupItem, index?: number): string {
		const currentProperty = firstProperty.find((p) => p.id === secondProperty.id);

		if (currentProperty.images?.length && typeof index === 'number') {
			return currentProperty.images[index]?.url;
		}

		return currentProperty.image;
	}
}
